import React, { useContext, useEffect, useState } from 'react'
import { CFormSwitch } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilPencil } from '@coreui/icons'
import moment from 'moment'
import SearchTable from 'src/components/searchTable'
import { GlobalContext } from '../../context/GlobalState'
import { PERMISSIONS } from '../../utils/permissions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDoubleDown } from '@fortawesome/free-solid-svg-icons'
import { checkPermissions, raiseToast } from '../../utils/helper'
import useAxios from 'axios-hooks'
import { ACCOUNT_CONFIG_API, COUNTRY_API, SUBSCRIPTION_API } from '../../utils/urls'
import { AppContext } from '../../context/app'
import SubscriptionCreateModal from './SubscriptionCreationModal'

const Subscription = () => {
  const { setLoader, accountObj } = useContext(AppContext)
  const language = localStorage.getItem('language')
  const [subscriptions, setSubscriptions] = useState([])
  const [openModal, setOpenModal] = useState(false)
  const [selectedSub, setSelectedSub] = useState({})

  const [countries, setCountries] = useState([])
  const [userCurrency, setUserCurrency] = useState([])

  const { userSessionObj } = useContext(GlobalContext)
  const defaultLangCheck = language === accountObj?.default_language
  const createAllowed =
    checkPermissions(userSessionObj.session_data.permissions, PERMISSIONS.PRICING.CREATE) &&
    defaultLangCheck
  const updateAllowed = checkPermissions(
    userSessionObj.session_data.permissions,
    PERMISSIONS.PRICING.UPDATE,
  )

  const [{ data: subscriptionList, loading: subscriptionListLoading }, subscriptionListCall] =
    useAxios(
      {
        url: SUBSCRIPTION_API + '?type=normal',
        method: 'get',
      },
      { manual: true },
    )

  const [{ data: countryList, loading: countryLoading }] = useAxios({
    url: COUNTRY_API,
    method: 'get',
  })

  const [{ data: accountConfigData, loading: accountConfigLoading }] = useAxios({
    url: ACCOUNT_CONFIG_API,
    method: 'get',
  })

  const [{ data: createSubscription, loading: createSubscriptionLoading }, subscriptionCreateCall] =
    useAxios(
      {
        url: SUBSCRIPTION_API,
        method: 'post',
      },
      { manual: true },
    )

  const [{ data: updateSubscription, loading: updateSubscriptionLoading }, subscriptionUpdateCall] =
    useAxios(
      {
        url: SUBSCRIPTION_API,
        method: 'put',
      },
      { manual: true },
    )

  useEffect(() => {
    subscriptionListCall()
  }, [])

  useEffect(() => {
    setLoader(
      accountConfigLoading ||
        countryLoading ||
        subscriptionListLoading ||
        createSubscriptionLoading ||
        updateSubscriptionLoading,
    )
  }, [
    accountConfigLoading,
    countryLoading,
    subscriptionListLoading,
    createSubscriptionLoading,
    updateSubscriptionLoading,
  ])

  useEffect(() => {
    if (subscriptionList && subscriptionList.success) {
      let { data } = subscriptionList
      let tempArr = []
      data.forEach((ele) => {
        let tempEle = ele
        tempEle[`days`] = `${ele.duration} Days`
        tempEle[`subFee`] = `${ele.amount} ${ele.currency}`
        tempArr.push(tempEle)
      })
      setSubscriptions(tempArr)
    }
  }, [subscriptionList])

  useEffect(() => {
    if (countryList && countryList.success) {
      setCountries(countryList.data)
    }
  }, [countryList])

  useEffect(() => {
    if (accountConfigData && accountConfigData.success) {
      setUserCurrency(accountConfigData.data.map((item) => item.currency))
    }
  }, [accountConfigData])

  useEffect(() => {
    if (createSubscription && createSubscription.success) {
      subscriptionListCall()
      setOpenModal(false)
      raiseToast('success', 'Subscription created successfully')
    }
  }, [createSubscription])

  useEffect(() => {
    if (updateSubscription && updateSubscription.success) {
      subscriptionListCall()
      setOpenModal(false)
      raiseToast('success', 'Subscription updated successfully')
    }
  }, [updateSubscription])

  const handleEdit = (info) => {
    if (!updateAllowed) {
      raiseToast('error', "You don't have enough permission")
      return
    }
    setSelectedSub(info)
    setOpenModal(true)
  }

  const handleStatus = async (info) => {
    if (!updateAllowed) {
      raiseToast('error', "You don't have enough permission")
      return
    }
    subscriptionUpdateCall({
      params: {
        subscription_id: info._id,
      },
      data: {
        status: !info.status,
      },
    })
  }

  const addSubscription = (payload) => {
    subscriptionCreateCall({
      data: payload,
    })
    setOpenModal(false)
  }

  const editSubscription = (payload, id) => {
    subscriptionUpdateCall({
      params: {
        subscription_id: id,
      },
      data: payload,
    })
    setOpenModal(false)
  }

  const tableConfig = [
    {
      header: 'Plan',
      dataCell: (info) => info.name,
      width: 20,
    },
    {
      header: 'Duration',
      dataCell: (info) => info.days,
      width: 18,
    },
    {
      header: 'Subscription Fee',
      dataCell: (info) => info.subFee,
      width: 20,
    },
    {
      header: 'Created On',
      dataCell: (info) => moment.utc(info.created_date).format('DD-MM-YYYY'),
      width: 15,
    },
    {
      header: 'Status',
      dataCell: (info) => (
        <CFormSwitch
          label={info.status ? 'Active' : 'Disabled'}
          checked={info.status}
          valid={info.status}
          value={info.status}
          onChange={() => handleStatus(info)}
          size="lg"
        />
      ),
      width: 15,
    },
    {
      header: <FontAwesomeIcon icon={faAngleDoubleDown} size="lg" className="mx-1" />,
      dataCell: (info) => (
        <CIcon
          icon={cilPencil}
          style={{ cursor: 'pointer' }}
          size="lg"
          title="Edit"
          onClick={() => handleEdit(info)}
        />
      ),
      width: 7,
    },
  ]

  return (
    <>
      <h4>Subscription</h4>
      <p>Create subscription plan that best suited your need.</p>
      <SearchTable
        type={`Subscription`}
        tableCardHeight={65}
        disableButton={false}
        buttonName={`+ Subscription`}
        buttonFunction={() => setOpenModal(!openModal)}
        searchTerm={`name`}
        tableContent={subscriptions}
        tableConfig={tableConfig}
        createPermission={createAllowed}
        disabledButtonTitle={
          defaultLangCheck ? '' : 'Adding Subscription Available on Default Language'
        }
      />
      {openModal && (
        <SubscriptionCreateModal
          data={selectedSub}
          currencies={userCurrency}
          countries={countries}
          createCallback={addSubscription}
          updateCallback={editSubscription}
          openModal={openModal}
          entity={'Subscription'}
          handleClose={() => {
            setSelectedSub({})
            setOpenModal(false)
          }}
        />
      )}
    </>
  )
}

export default Subscription
